/**
 * Iter137 — Zefix Handelsregister-Autocomplete.
 *
 * Kleines Suchfeld, das gegen /api/zefix/search geht. Bei Klick auf ein
 * Ergebnis wird onSelect({name, uid, uid_formatted, city, legal_form}) gerufen.
 */
import React, { useState, useRef, useEffect } from "react";
import { apiClient } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Building2, Download } from "lucide-react";
import { downloadPost, blobErrorDetail } from "@/lib/downloadPost";
import { toast } from "sonner";

export default function ZefixLookup({ onSelect, placeholder = "Firma im Handelsregister suchen…" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  const exportResults = async () => {
    try {
      const name = await downloadPost("/zefix/export-search", { query, results });
      toast.success(`${name} heruntergeladen`);
    } catch (e) {
      toast.error(await blobErrorDetail(e));
    }
  };

  useEffect(() => {
    // Klick außerhalb schließt Dropdown
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const doSearch = async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const r = await apiClient.get(`/zefix/search?q=${encodeURIComponent(q.trim())}`);
      setResults(r.data || []);
      setOpen(true);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(v), 350);
  };

  const pick = async (r) => {
    setQuery(r.name);
    setOpen(false);
    // Iter142: BFS UID-Register für vollständige Adresse (Straße + PLZ)
    let enriched = r;
    if (r.uid) {
      try {
        const bfs = await apiClient.get(`/uid/${encodeURIComponent(r.uid)}`);
        // Nur setzen wenn BFS Adress-Daten hat; nie überschreiben mit null
        enriched = {
          ...r,
          street: bfs.data.street || r.street,
          postal_code: bfs.data.postal_code || r.postal_code,
          city: bfs.data.city || r.city,
          hr_number: bfs.data.hr_number,
          vat_active: bfs.data.vat_active,
        };
      } catch (e) {
        // BFS Fehler → einfach nur Zefix-Daten zurückliefern (silent)
        console.warn("BFS enrichment failed:", e?.response?.status);
      }
    }
    onSelect?.(enriched);
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid="zefix-lookup">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          value={query}
          onChange={onChange}
          onFocus={() => query.length >= 2 && setOpen(true)}
          placeholder={placeholder}
          className="pl-9"
          data-testid="zefix-input"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
        )}
      </div>

      {open && results.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-y-auto"
          data-testid="zefix-results"
        >
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 bg-slate-50 sticky top-0">
            <span className="text-[11px] text-slate-500">{results.length} Treffer im Handelsregister</span>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={exportResults}
              className="text-[11px] text-indigo-700 hover:underline inline-flex items-center gap-1"
              data-testid="zefix-export-search-btn"
            >
              <Download className="h-3 w-3" /> CSV exportieren
            </button>
          </div>
          {results.map((r) => (
            <button
              key={r.uid || r.ehraid || r.name}
              type="button"
              onClick={() => pick(r)}
              className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-slate-100 last:border-0 flex items-start gap-2"
              data-testid={`zefix-result-${r.uid}`}
            >
              <Building2 className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{r.name}</div>
                <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                  {r.uid_formatted && <span className="font-mono">{r.uid_formatted}</span>}
                  {r.legal_form && <span className="bg-slate-100 px-1.5 py-0.5 rounded">{r.legal_form}</span>}
                  {r.city && <span>{r.city}</span>}
                  {r.canton && <span className="text-slate-400">({r.canton})</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && !loading && query.length >= 2 && results.length === 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs text-slate-500">
          Keine Treffer im Handelsregister für &bdquo;{query}&ldquo;
        </div>
      )}
    </div>
  );
}
